import { useState, useRef, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/button'
import { Send, Undo2, Bot, User, Sparkles } from 'lucide-react'

interface EditMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  changes?: any[]
}

interface Props {
  websiteId: string
  activePageId?: string
  selectedBlockId?: string | null
  messages: EditMessage[]
  isEditing: boolean
  streamingText: string
  onSend: (instruction: string, pageId?: string, blockId?: string) => void
  onUndo: () => any[] | null
  onRefresh: () => void
}

export default function WebsiteAIPanel({ websiteId, activePageId, selectedBlockId, messages, isEditing, streamingText, onSend, onUndo, onRefresh }: Props) {
  const { t } = useI18n()
  const [input, setInput] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  const handleSend = () => {
    const text = input.trim()
    if (!text || isEditing) return
    onSend(text, activePageId, selectedBlockId || undefined)
    setInput('')
  }

  const handleUndo = async () => {
    const changes = onUndo()
    if (changes) {
      await onRefresh()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const quickActions = [
    t('修改英雄区标题', 'Change hero title'),
    t('添加一个FAQ区块', 'Add a FAQ section'),
    t('更换主题色为蓝色', 'Change theme to blue'),
    t('添加联系我们页面', 'Add contact page'),
  ]

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && !isEditing ? (
          <div className="text-center py-8">
            <div className="w-10 h-10 rounded-2xl bg-orange-500/10 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="h-5 w-5 text-orange-500" />
            </div>
            <p className="text-xs text-gray-400 mb-4">{t('用自然语言编辑网站', 'Edit your website with natural language')}</p>
            <div className="space-y-1.5">
              {quickActions.map((action, i) => (
                <button key={i} onClick={() => { setInput(action) }}
                  className="w-full text-left px-3 py-2 text-xs rounded-xl bg-black/[0.02] text-gray-500 hover:bg-orange-50/50 hover:text-orange-500 transition-all">
                  {action}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="h-3 w-3 text-orange-500" />
                  </div>
                )}
                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-orange-500 text-white rounded-br-md'
                    : 'bg-white border border-black/[0.04] text-gray-600 rounded-bl-md'
                }`}>
                  {msg.content}
                  {msg.changes && msg.changes.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-black/[0.06] text-[10px] text-gray-400">
                      {msg.changes.length} {t('处修改', 'changes')}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isEditing && streamingText && (
              <div className="flex gap-2 justify-start">
                <div className="w-6 h-6 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="h-3 w-3 text-orange-500" />
                </div>
                <div className="max-w-[85%] px-3 py-2 rounded-xl rounded-bl-md bg-white border border-black/[0.04] text-xs text-gray-600 leading-relaxed">
                  {streamingText}
                  <span className="inline-block w-1 h-3 bg-orange-500 animate-pulse ml-0.5 align-middle" />
                </div>
              </div>
            )}

            {isEditing && !streamingText && (
              <div className="flex gap-2 justify-start">
                <div className="w-6 h-6 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Bot className="h-3 w-3 text-orange-500" />
                </div>
                <div className="px-3 py-2 rounded-xl rounded-bl-md bg-white border border-black/[0.04]">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-black/[0.04]">
        {messages.length > 0 && (
          <button onClick={handleUndo}
            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-orange-500 mb-2 transition-colors">
            <Undo2 className="h-3 w-3" />
            {t('撤销上次修改', 'Undo last edit')}
          </button>
        )}
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('描述你想修改的内容...', 'Describe what you want to change...')}
            disabled={isEditing}
            className="flex-1 h-9 px-3 rounded-xl bg-white border border-black/[0.06] text-sm placeholder:text-gray-300 focus:border-orange-200 focus:outline-none disabled:opacity-50"
          />
          <Button onClick={handleSend} disabled={!input.trim() || isEditing}
            className="shrink-0 h-9 w-9 p-0 bg-orange-500 hover:bg-orange-600 rounded-xl">
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
